import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { loginStateAtom } from '../../../AtomStore';
import '../../recruit/common/Recruit.scss';
import '../../recruit/common/RecruitList.scss';
import MainURL from '../../../MainURL';
import { citydata } from '../../../DefaultData';
import { religiousbodySubSort } from '../../recruit/recruit_minister/RecruitMinisterPostData';
import { useKakaoAddress } from '../../recruit/common/useKakaoAddress';
import { pastorApi, errorMessage } from '../pastorApi';
import type { CallRequest } from '../pastorApi';
import { CHURCH_SIZES, NEEDS, REGION_TYPES } from '../pastorOptions';
import Loading from '../../../components/Loading';

const EMPTY: CallRequest = {
  churchName: '', denomination: '', sido: '', sigungu: '', regionType: '', address: '', attendance: '', foundedYear: null,
  intro: '', offerConditions: [], salaryNote: '', desiredNote: '',
  managerName: '', managerRole: '', managerPhone: '', managerEmail: '', allowPromote: true,
};

const CITIES: string[] = citydata.map((c: { city: string }) => c.city);
const subareasOf = (city: string): string[] => citydata.find((c: { city: string }) => c.city === city)?.subarea || [];

// 카카오 주소의 시·도 약칭(경북·충남…) → 공고 등록의 시·도 이름(경상북도·충청남도…)
const SIDO_ALIAS: Record<string, string> = {
  충북: '충청북도', 충남: '충청남도', 경북: '경상북도', 경남: '경상남도', 전남: '전라남도', 전북: '전북특별자치도', 강원: '강원특별자치도', 제주: '제주특별자치도',
};
function matchRegion(location: string) {
  const [sido = '', ...rest] = location.split(' ');
  const sigungu = rest.join(' ');
  const city = CITIES.find((c) => c === (SIDO_ALIAS[sido] || sido)) || CITIES.find((c) => c.startsWith(sido)) || '';
  const sub = subareasOf(city).find((s) => sigungu.startsWith(s) || s.startsWith(sigungu.split(' ')[0])) || '';
  return { city, sub };
}

const TEXTAREA_STYLE: React.CSSProperties = {
  width: '100%', minHeight: 160, padding: '12px 14px', boxSizing: 'border-box', fontSize: 16, lineHeight: 1.6,
  border: '1px solid var(--rule-2)', borderRadius: 'var(--r)', fontFamily: 'inherit', resize: 'vertical',
};

// 공고 등록의 체크 목록(교단·위치 등)과 같은 모양
function CheckRow({ options, isOn, onToggle, image }: { options: string[]; isOn: (v: string) => boolean; onToggle: (v: string) => void; image?: (v: string) => string }) {
  return (
    <div className="checkTextRow">
      {options.map((item) => (
        <div key={item} className="checkTextBox" onClick={() => onToggle(item)}>
          <input className="checkbox" type="checkbox" checked={isOn(item)} readOnly />
          <span className="checkmark" />
          <div className="imgtextBox">
            {image && <img src={image(item)} alt="" />}
            <p className={isOn(item) ? 'selected' : ''}>{item}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// 교회 — 담임목사 청빙 의뢰서. 로그인 없이도 낼 수 있다 (화면 구성은 구인정보 > 공고 등록과 같다)
// 사역자모아가 담임목회 희망 사역자 중 맞는 분을 찾아, 그분이 동의하면 이력서를 전달한다
// ?edit=<id> — 로그인해서 낸 의뢰를 수정 (내 청빙 현황에서 진입)
export default function ChurchRequest() {
  const navigate = useNavigate();
  const isLogin = useAtomValue(loginStateAtom);
  const editId = Number(useSearchParams()[0].get('edit')) || null;
  const [form, setForm] = useState<CallRequest>(EMPTY);
  const [loadingEdit, setLoadingEdit] = useState(Boolean(editId));
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const set = <K extends keyof CallRequest>(key: K, value: CallRequest[K]) => setForm((f) => ({ ...f, [key]: value }));
  const text = (key: keyof CallRequest) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => set(key, e.target.value as never);

  const kakao = useKakaoAddress({
    onRegion: (location) => {
      const { city, sub } = matchRegion(location);
      if (city) setForm((f) => ({ ...f, sido: city, sigungu: sub }));
    },
  });

  const { resetFromSaved } = kakao;
  useEffect(() => {
    if (!editId) return;
    if (!isLogin) { setLoadingEdit(false); return; }
    pastorApi.myRequests()
      .then((items) => {
        const r = items.find((x) => x.id === editId);
        if (!r) return;
        setForm({ ...EMPTY, ...r });
        resetFromSaved(r.address || '');
      })
      .catch(() => {})
      .finally(() => setLoadingEdit(false));
  }, [editId, isLogin, resetFromSaved]);

  if (editId && loadingEdit) return <div className="pastor-call inner pc-page"><Loading /></div>;
  if (editId && !form.id) {
    return (
      <div className="pastor-call inner pc-page">
        <div className="pc-gate">
          <p className="pc-gate__title">수정할 의뢰를 찾을 수 없습니다</p>
          <p>{isLogin ? '삭제되었거나 이 계정으로 낸 의뢰가 아닙니다.' : '의뢰를 낸 계정으로 로그인해 주세요.'}</p>
          <button type="button" className="mm-btn mm-btn--primary pc-btn-lg" onClick={() => navigate(isLogin ? '/pastor/my' : '/login')}>{isLogin ? '내 청빙 현황' : '로그인하기'}</button>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="pastor-call inner pc-page">
        <div className="pc-gate">
          <p className="pc-gate__title">청빙 의뢰가 접수되었습니다</p>
          <p>
            사역자모아가 담임목회를 희망하는 목사님 가운데 교회와 맞는 분을 찾아 먼저 여쭙겠습니다.<br />
            목사님이 동의하시면 담당자 이메일로 이력서를 보내 드립니다.
          </p>
          {isLogin ? (
            <button type="button" className="mm-btn mm-btn--primary pc-btn-lg" onClick={() => navigate('/pastor/my')}>내 청빙 현황 보기</button>
          ) : (
            <button type="button" className="mm-btn mm-btn--outline pc-btn-lg" onClick={() => navigate('/pastor')}>처음으로</button>
          )}
        </div>
      </div>
    );
  }

  const save = async () => {
    if (!form.churchName.trim()) return alert('교회명을 입력해 주세요.');
    if (!form.managerName.trim()) return alert('담당자 이름을 입력해 주세요.');
    if (!form.managerPhone.trim()) return alert('담당자 연락처를 입력해 주세요.');
    if (!form.managerEmail.trim()) return alert('후보 이력서를 받을 담당자 이메일을 입력해 주세요.');
    setSaving(true);
    try {
      const body = { ...form, address: kakao.getFullAddress() || form.address };
      if (editId) {
        await pastorApi.updateRequest(editId, body);
        alert('청빙 의뢰서를 수정했습니다.');
        navigate('/pastor/my');
        return;
      }
      await pastorApi.createRequest(body);
      setDone(true);
      window.scrollTo(0, 0);
    } catch (e) {
      alert(errorMessage(e, editId ? '수정하지 못했습니다.' : '접수하지 못했습니다. 청빙 대행 상담으로 연락해 주세요.'));
    } finally {
      setSaving(false);
    }
  };

  const toggleCondition = (v: string) =>
    set('offerConditions', form.offerConditions.includes(v) ? form.offerConditions.filter((x) => x !== v) : [...form.offerConditions, v]);

  return (
    <div className="recruit">
      <div className="inner">
        <div className="subpage__main">

          <section style={{ marginBottom: '50px' }}>
            <div className="main_title_row">
              <h3 className="main_title">{editId ? '청빙 의뢰서 수정' : '담임목사 청빙 의뢰서'}</h3>
              <div className="postBtnbox">
                <div className="postBtn" style={{ marginRight: '10px' }} onClick={() => navigate(-1)}>
                  <p>뒤로</p>
                </div>
              </div>
            </div>
            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--ink-3)', margin: '12px 0 0' }}>
              사역자모아가 담임목회를 희망하는 목사님 가운데 교회와 맞는 분을 찾아 이력서를 전달해 드립니다. 교회의 형편을 솔직하게 적어 주실수록 잘 맞는 분을 찾을 수 있습니다.{!isLogin && ' 로그인하시면 의뢰 진행 현황과 전달받은 이력서를 사이트에서도 보실 수 있습니다.'}
            </p>
          </section>

          {/* 교회정보 -------------------------------------------------------------------------- */}
          <section>
            <h1 className="section_title">교회정보</h1>
            <div className="inputCover">
              <div className="inputBox">
                <h3 className="title"><p>교회명</p></h3>
                <div className="inputRow">
                  <input value={form.churchName} className="inputdefault" type="text" onChange={text('churchName')} />
                </div>
              </div>
              <div className="inputBox">
                <h3 className="title"><p>교단</p></h3>
                <div className="inputRow">
                  <CheckRow
                    options={religiousbodySubSort}
                    isOn={(v) => form.denomination === v}
                    onToggle={(v) => set('denomination', form.denomination === v ? '' : v)}
                    image={(v) => `${MainURL}/siteimages/religiousbody/${v}.jpg`}
                  />
                </div>
              </div>
              <div className="inputBox">
                <h3 className="title"><p>위치</p></h3>
                <div className="inputRow">
                  <CheckRow
                    options={CITIES}
                    isOn={(v) => form.sido === v}
                    onToggle={(v) => setForm((f) => ({ ...f, sido: f.sido === v ? '' : v, sigungu: '' }))}
                  />
                </div>
              </div>
              <div className="inputBox">
                <h3 className="title"><p>상세위치</p></h3>
                <div className="inputRow">
                  {!form.sido ? (
                    <p style={{ fontSize: '16px', color: 'var(--ink-4)' }}>위치를 선택해주세요</p>
                  ) : (
                    <CheckRow
                      options={subareasOf(form.sido)}
                      isOn={(v) => form.sigungu === v}
                      onToggle={(v) => set('sigungu', form.sigungu === v ? '' : v)}
                    />
                  )}
                </div>
              </div>
              <div className="inputBox">
                <h3 className="title"><p>주소</p></h3>
                <div className="inputRow recruit-address-fields">
                  <div className="recruit-address-row recruit-address-row--postcode">
                    <input
                      value={kakao.postcode}
                      className="inputdefault recruit-address-postcode"
                      type="text"
                      placeholder="우편번호"
                      readOnly
                      onClick={() => { if (!kakao.postcode.trim()) kakao.handleAddressSearch(); }}
                    />
                    <button type="button" className="recruit-address-search-btn" onClick={kakao.handleAddressSearch}>우편번호 찾기</button>
                  </div>
                  <input value={kakao.address} className="inputdefault recruit-address-input" type="text" placeholder="주소" readOnly />
                  {kakao.addressExtra.trim() !== '' && (
                    <input value={kakao.addressExtra} className="inputdefault recruit-address-extra-input" type="text" placeholder="참고항목" readOnly />
                  )}
                  {kakao.addressGuide.trim() !== '' && <p className="recruit-address-guide">{kakao.addressGuide}</p>}
                  <input
                    ref={kakao.addressDetailRef}
                    value={kakao.addressDetail}
                    className="inputdefault recruit-address-detail-input"
                    type="text"
                    placeholder="상세주소"
                    onChange={(e) => kakao.setAddressDetail(e.target.value)}
                  />
                </div>
              </div>
              <div className="inputBox">
                <h3 className="title"><p>지역 유형</p></h3>
                <div className="inputRow">
                  <CheckRow options={REGION_TYPES} isOn={(v) => form.regionType === v} onToggle={(v) => set('regionType', form.regionType === v ? '' : v)} />
                </div>
              </div>
              <div className="inputBox">
                <h3 className="title"><p>출석 규모</p></h3>
                <div className="inputRow">
                  <CheckRow options={CHURCH_SIZES} isOn={(v) => form.attendance === v} onToggle={(v) => set('attendance', form.attendance === v ? '' : v)} />
                </div>
              </div>
              <div className="inputBox">
                <h3 className="title"><p>설립 연도</p></h3>
                <div className="inputRow">
                  <input
                    value={form.foundedYear ?? ''}
                    className="inputdefault"
                    type="text"
                    inputMode="numeric"
                    placeholder="예) 1962"
                    onChange={(e) => set('foundedYear', Number(e.target.value.replace(/[^\d]/g, '')) || null)}
                  />
                </div>
              </div>
              <div className="inputBox">
                <h3 className="title"><p>교회 소개</p></h3>
                <div className="inputRow">
                  <textarea
                    value={form.intro}
                    style={TEXTAREA_STYLE}
                    onChange={text('intro')}
                    placeholder="교회의 역사, 성도 구성(연령대), 지역 환경, 청빙 배경 등을 적어 주세요."
                  />
                </div>
              </div>
            </div>
          </section>

          {/* 청빙조건 -------------------------------------------------------------------------- */}
          <section>
            <h1 className="section_title">청빙조건</h1>
            <div className="inputCover">
              <div className="inputBox">
                <h3 className="title"><p>제공 조건</p></h3>
                <div className="inputRow">
                  <CheckRow options={NEEDS} isOn={(v) => form.offerConditions.includes(v)} onToggle={toggleCondition} />
                </div>
              </div>
              <div className="inputBox">
                <h3 className="title"><p>사례비·처우</p></h3>
                <div className="inputRow">
                  <input value={form.salaryNote} className="inputdefault" type="text" onChange={text('salaryNote')} placeholder="예) 월 250만원, 사택(3룸) 제공, 차량 유지비 지원" />
                </div>
              </div>
              <div className="inputBox">
                <h3 className="title"><p>원하는 목사님</p></h3>
                <div className="inputRow">
                  <textarea
                    value={form.desiredNote}
                    style={TEXTAREA_STYLE}
                    onChange={text('desiredNote')}
                    placeholder="예) 어르신이 많은 교회라 심방에 힘써 주실 50대 목사님, 사모님과 함께 오실 수 있는 분이면 좋겠습니다."
                  />
                </div>
              </div>
              <div className="inputBox">
                <h3 className="title"><p>홍보 동의</p></h3>
                <div className="inputRow">
                  <CheckRow
                    options={['담임목회 희망 목사님들께 이 청빙 소식을 알려도 됩니다']}
                    isOn={() => form.allowPromote}
                    onToggle={() => set('allowPromote', !form.allowPromote)}
                  />
                  <p style={{ fontSize: 15, color: 'var(--ink-3)', margin: '8px 0 0' }}>
                    동의하시면 이력서를 등록한 목사님들께 교회명·지역·조건이 안내되고, 관심 있는 목사님의 이력서도 사역자모아가 검토해 전달합니다. 담당자 연락처는 알리지 않습니다.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* 담당자정보 ------------------------------------------------------------------------ */}
          <section>
            <h1 className="section_title">담당자정보</h1>
            <div className="inputCover">
              <p style={{ fontSize: 15, color: 'var(--ok)', fontWeight: 600, margin: '0 0 24px' }}>
                🔒 담당자 연락처는 사역자모아만 보관합니다. 후보 목사님의 이력서는 담당자 이메일로 전달됩니다.
              </p>
              <div className="inputBox">
                <h3 className="title"><p>담당자 이름</p></h3>
                <div className="inputRow">
                  <input value={form.managerName} className="inputdefault" type="text" onChange={text('managerName')} />
                </div>
              </div>
              <div className="inputBox">
                <h3 className="title"><p>직분</p></h3>
                <div className="inputRow">
                  <input value={form.managerRole} className="inputdefault" type="text" onChange={text('managerRole')} placeholder="예) 청빙위원장 장로" />
                </div>
              </div>
              <div className="inputBox">
                <h3 className="title"><p>연락처</p></h3>
                <div className="inputRow">
                  <input value={form.managerPhone} className="inputdefault" type="text" inputMode="tel" onChange={text('managerPhone')} placeholder="010-0000-0000" />
                </div>
              </div>
              <div className="inputBox">
                <h3 className="title"><p>이메일</p></h3>
                <div className="inputRow">
                  <input value={form.managerEmail} className="inputdefault" type="text" inputMode="email" onChange={text('managerEmail')} placeholder="후보 이력서를 받을 이메일" />
                </div>
              </div>
            </div>
          </section>

          <div className="submitBtnWrap">
            <button type="button" className="submitBtn" disabled={saving} onClick={save}>
              {saving ? (editId ? '저장 중…' : '접수 중…') : editId ? '수정 내용 저장' : '청빙 의뢰하기'}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}
